import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { extractNextLaunchContext } from '@/utils/nextLaunch'
import { isLocalRuntime, showComingSoonFeatures } from '@/utils/runtimeAccess'

const showLocalOnlyRoutes = isLocalRuntime

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: showLocalOnlyRoutes ? 'demo' : 'root',
      component: showLocalOnlyRoutes
        ? () => import('../views/DemoLaunchView.vue')
        : () => import('../views/LaunchView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/admin',
      name: 'admin-login',
      component: () => import('../views/AdminLoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/launch/:userGuid?',
      name: 'launch',
      component: () => import('../views/LaunchView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/demo',
      name: showLocalOnlyRoutes ? 'demo-alt' : 'demo',
      component: () => import('../views/DemoLaunchView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/app',
      component: () => import('../views/AppLayout.vue'),
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'app',
          redirect: '/app/gallery'
        },
        {
          path: 'gallery',
          name: 'gallery',
          component: () => import('../views/ReportGalleryView.vue')
        },
        {
          path: 'reports',
          name: 'reports',
          component: () => import('../views/ReportsView.vue')
        },
        {
          path: 'report-builder',
          name: 'report-builder',
          component: () => import('../views/ReportBuilderView.vue')
        },
        {
          path: 'saved-reports',
          name: 'saved-reports',
          component: () => import('../views/SavedReportsView.vue')
        },
        {
          path: 'reports/results/:reportId',
          name: 'report-results',
          component: () => import('../views/ReportResultsView.vue')
        },
        {
          path: 'settings',
          name: 'settings',
          component: () => import('../views/SettingsView.vue')
        },
        {
          path: 'data-explorer',
          name: 'data-explorer',
          component: () => import('../views/DataExplorerView.vue'),
          meta: { requiresAuth: true, requiresAdminRole: true }
        },
        // Chart and Crosstab — available everywhere (BETA)
        {
          path: 'crosstab-builder',
          name: 'crosstab-builder',
          component: () => import('../views/CrosstabBuilderView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'crosstab-viewer/:id',
          name: 'crosstab-viewer',
          component: () => import('../views/CrosstabViewerView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'chart-builder',
          name: 'chart-builder',
          component: () => import('../views/ChartBuilderView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'chart-viewer/:id',
          name: 'chart-viewer',
          component: () => import('../views/ChartViewerView.vue'),
          meta: { requiresAuth: true }
        },
        {
          path: 'data-health',
          name: 'data-health',
          component: () => import('../views/DataHealthView.vue'),
          meta: { requiresAuth: true, requiresAdminRole: true }
        },
        // Coming Soon features — hidden in production only
        ...(showComingSoonFeatures ? [
          {
            path: 'chat',
            name: 'chat',
            component: () => import('../views/ChatView.vue'),
            meta: { requiresAuth: true, requiresAdminRole: true }
          },
          {
            path: 'playground',
            name: 'playground',
            component: () => import('../views/PlaygroundView.vue'),
            meta: { requiresAuth: true, devOnly: true }
          },
          {
            path: 'dev-import',
            name: 'dev-import',
            component: () => import('../views/DevImportView.vue'),
            meta: { requiresAuth: true, devOnly: true }
          },
          {
            path: 'sales-upload-playground',
            name: 'sales-upload-playground',
            component: () => import('../views/SalesUploadPlayground.vue'),
            meta: { requiresAuth: true, devOnly: true }
          },
          {
            path: 'firestore-inspector',
            name: 'firestore-inspector',
            component: () => import('../views/FirestoreInspectorView.vue'),
            meta: { requiresAuth: true, devOnly: true }
          },
          {
            path: 'system-health',
            name: 'system-health',
            component: () => import('../views/SystemHealthView.vue'),
            meta: { requiresAuth: true, devOnly: true }
          },
          {
            path: 'ai-chat-debug',
            name: 'ai-chat-debug',
            component: () => import('../views/AIChatDebugView.vue'),
            meta: { requiresAuth: true, devOnly: true }
          },
          {
            path: 'prod-report-migration',
            name: 'prod-report-migration',
            component: () => import('../views/ProdReportMigrationView.vue'),
            meta: { requiresAuth: true, devOnly: true }
          }
        ] : [])
      ]
    }
  ]
})

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore()
  const nextLaunchContext = extractNextLaunchContext(to.query)

  if (nextLaunchContext) {
    authStore.applyNextLaunchContext(nextLaunchContext)
  }

  // If a handoff token is present, always redirect to launch to re-authenticate
  // (even if already authenticated — the token may be for a different tenant)
  const handoffToken = to.query.handoffToken as string | undefined
  if (handoffToken && to.name !== 'launch') {
    next({ name: 'launch', query: to.query })
    return
  }
  
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    if (nextLaunchContext || authStore.launchContext?.sourceApp === 'next') {
      next({ name: 'launch', query: to.query })
      return
    }

    next({ name: showLocalOnlyRoutes ? 'demo' : 'launch' })
  } else if (to.meta.requiresAdminRole) {
    const roles = authStore.user?.roles || []
    const isAdmin = authStore.user?.isVendor === true || roles.some(r => r === 'Alliance' || r === 'Tenant')
    if (!isAdmin) {
      next({ name: 'gallery' })
      return
    }
    next()
  } else {
    next()
  }
})

// Handle stale chunk errors after deploy (old JS references new filenames that don't exist)
router.onError((error, to) => {
  if (error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed')) {
    // Force reload to get the new asset manifest
    window.location.href = to.fullPath
  }
})

// Track route changes in Grafana Faro
router.afterEach((to) => {
  try {
    const faro = (window as any).GrafanaFaroWebSdk?.faro ?? (window as any).__FARO_INSTANCE__
    const pageName = (to.name as string) || to.path
    if (faro?.api?.setView) {
      faro.api.setView({ name: pageName })
    }
  } catch { /* non-critical */ }
})

export default router
